/**
 * 页面信息解析工具
 * 完全独立于旧版，用于解析当前页面名称
 */

import { match } from "path-to-regexp";
import { PinDetailContext } from "../context/PinDetailContext";

export interface PageInfo {
  page_nm: string;
  sub_page_nm?: string;
  page_params?: Record<string, any>;
}

interface RouteRule {
  path: string;
  name: string | ((router: any) => string);
  subName?: string | ((router: any) => string);
}

/**
 * URL 路由规则映射
 * 根据 URL 路径匹配页面名称
 */
const URL_MAP: RouteRule[] = [
  { path: "/", name: "首页" },
  {
    path: "/discovery/:categoryId?/:type?",
    name: "发现页",
    subName: (router) => {
      const { type } = router.params;
      return type === "boards" ? "画板" : "采集";
    },
  },
  {
    path: "/discovery/boards/:boardId",
    name: "发现页",
    subName: () => "采集",
  },
  {
    path: "/search",
    name: "搜索结果页",
    subName: (router) => {
      const { type } = router.query;
      const typeMap: Record<string, string> = {
        board: "画板",
        user: "用户",
        material: "花瓣素材",
        aigc: "AIGC",
        pin: "采集",
      };
      return typeMap[type] || "采集";
    },
  },
  {
    path: "/pins/:pinId",
    name: "采集详情页",
    subName: () => {
      // 优先从 PinDetailContext 获取 content_type（新方案，不依赖 DOM）
      const pinDetailContext = PinDetailContext.get();
      if (pinDetailContext?.content_type) {
        return pinDetailContext.content_type;
      }

      // 兜底：如果 PinDetailContext 未设置，返回默认值
      return "普通采集";
    },
  },
  {
    path: "/boards/:boardId",
    name: "画板详情页",
    subName: (router) => {
      const { tab } = router.query;
      return tab === "follow" ? "被关注" : "采集";
    },
  },
  {
    path: "/user/:urlname/:openTab?/:tag?/:type?/:sub?",
    name: "个人页",
    subName: (router) => {
      const { openTab } = router.params;
      const pageMap: Record<string, string> = {
        pins: "采集",
        likes: "喜欢的",
        tags: "标签",
        creations: "原创",
        recycle: "回收站",
        boards: "画板",
        created: "创建的",
      };
      return pageMap[openTab] || "画板";
    },
  },
  {
    path: "/follow/:type?/:id?",
    name: "关注页",
    subName: (router) => {
      // 注意：旧版的 getPageData.js 中，type 参数在某些情况下可能是 undefined
      // 这是因为旧版的路由匹配逻辑可能不同，或者在某些情况下会忽略路由参数
      // 为了与旧版完全一致，我们需要让 type 为 undefined 时也返回 "全部"
      //
      // 但是，我们的路由匹配能正确解析出 type，所以我们需要特殊处理：
      // 当 URL 是 /follow/user/xxx 时，旧版返回 "全部"，新版也应该返回 "全部"
      //
      // 实际上，旧版在 pages/follow/index.jsx 中使用的是 source.sourceType（数字索引），
      // 而不是路由参数 type，所以旧版的 getPageData.js 中的 type 可能是 undefined
      //
      // 为了与旧版完全一致，我们直接返回 "全部"，忽略路由参数 type
      let type = router.params?.type;

      // 检查是否为新版 Feed（与旧版逻辑保持一致）
      // 注意：这里暂时依赖 DOM 属性，因为关注页的 isNewFeed 状态没有全局 Context
      // 如果后续需要完全废弃 DOM 依赖，可以考虑创建 FollowContext
      if (typeof window !== "undefined") {
        const feedEl = document.querySelector("[data-new-feed]");
        if (feedEl) {
          const isNewFeed = feedEl.getAttribute("data-new-feed");
          if (isNewFeed === "true") {
            // 为了与旧版完全一致，直接返回 "全部"
            // 旧版的 getPageData.js 中，type 参数在某些情况下是 undefined，
            // 导致 map[type] 是 undefined，所以返回 "全部"
            return "新版Feed-全部";
          }
        }
      }

      // 兜底：旧版 Feed
      return "旧版Feed";
    },
  },
  {
    path: "/space/:type?/:subType?",
    name: "灵感空间",
    subName: (router) => {
      const { type } = router.params;
      const typeMap: Record<string, string> = {
        boards: "画板",
        pins: "灵感",
        created: "我创建的",
        tags: "标签管理",
      };
      return typeMap[type] || "画板";
    },
  },
  { path: "/popular/:url?", name: "榜单页" },
  { path: "/materials", name: "花瓣素材首页" },
  { path: "/vip/info", name: "会员页" },
  { path: "/vip/buy", name: "会员支付页" },
  { path: "/settings", name: "个人设置页" },
  { path: "/guide", name: "新人引导页" },
  { path: "/explore/:topic", name: "专题页" },
  { path: "/topics/:urlname/:filterId?", name: "主题页" },
  { path: "/albums/:urlname", name: "主题合辑页" },
  { path: "/login", name: "登录页" },
  { path: "/create", name: "创建页" },
  { path: "/history/:type?", name: "浏览记录" },
  { path: "/about", name: "关于花瓣页" },
  { path: "/app", name: "App下载页" },
  { path: "/ai", name: "ai圈页面" },
  { path: "/calendars/:festival?", name: "热点日历聚合页" },
  { path: "/zhuanti/:keyword?", name: "精选专题" },
];

/**
 * 解析 URL 查询参数
 */
function parseQueryString(search: string): Record<string, string> {
  if (!search || search.length <= 1) return {};

  const query: Record<string, string> = {};
  const pairs = search.substring(1).split("&");

  for (const pair of pairs) {
    const [key, value] = pair.split("=");
    if (key) {
      query[decodeURIComponent(key)] = value ? decodeURIComponent(value) : "";
    }
  }

  return query;
}

/**
 * 获取当前页面信息
 * 根据 URL 路径匹配页面名称
 */
export function getPageInfo(): PageInfo {
  if (typeof window === "undefined") {
    return { page_nm: "" };
  }

  try {
    const pathname = window.location.pathname;
    const query = parseQueryString(window.location.search);

    for (const rule of URL_MAP) {
      const matchFn = match(rule.path, { decode: decodeURIComponent });
      const result = matchFn(pathname);

      if (!result) continue;

      const router = {
        params: result.params as Record<string, string>,
        query,
      };

      const pageInfo: PageInfo = {
        page_nm:
          typeof rule.name === "function" ? rule.name(router) : rule.name,
        page_params: query,
      };

      if (rule.subName) {
        pageInfo.sub_page_nm =
          typeof rule.subName === "function"
            ? rule.subName(router)
            : rule.subName;
      }

      return pageInfo;
    }

    return { page_nm: "未识别" };
  } catch (error) {
    console.error("[getPageInfo] Error:", error);
    return { page_nm: "" };
  }
}
